import React from "react";
import { Route, Switch } from "react-router-dom";

import { useProfile } from "app/context";
import { canViewCompaniesField, canViewSkillsField, isRecruiter } from "app/permissions";
import PrivateRoute from "components/common/PrivateRoute";
import Organizations from "components/internal/Organizations";
import { classnames } from "@culturehq/components";
import Users from "components/internal/Users";
import NotFound from "components/NotFound";
import Settings from "./Settings";
import Events from "./Events";
import Review from "./Review";
import Integrations from "./Integrations";
import Widgets from "./Widgets";
import AdminSidebar from "./AdminSidebar";
import Analytics from "./Analytics";
import UsersList from "./Users";
import RecognitionsOptions from "./RecognitionsOptions";
import StoryQuestion from "./settings/StoryQuestion";
import LandingPageTable from "./settings/LandingPageTable";
import EmployerBranding from "./settings/EmployerBranding";
import StoryApprovals from "./StoryApprovals";
import InternalAnalytics from "./InternalAnalytics";
import ExternalAnalytics from "./ExternalAnalytics/ExternalAnalytics";
import SlackUsers from "./SlackUsers";
import SocialAnalytics from "./SocialAnalytics/SocialAnalytics";
import AIContext from "./AIContext";
import DefaultPrompts from "./DefaultPrompts";
import PrivacyPolicies from "./PrivacyPolicies";
import SettingsJobMappings from "./SettingsJobMappings";
import SettingsJobMappers from "./SettingsJobMappers";
import UsersWithoutOrganization from "./UsersWithoutOrganization";
import VirtualBackgrounds from "./VirtualBackgrounds";
import BuilderTemplates from "./templates/BuilderTemplates";
import MonitorAIUsage from "./MonitorAIUsage";
import OrganizationMapperRoles from "./OrganizationMapperRoles";

const AdminSettings = () => {
  const { profile } = useProfile();
  const { organization } = profile;
  const {
    employerBranding, eventsEnabled, isEducational, recognitionsEnabled, externalMappedIds
  } = organization;

  return (
    <div className="Admin">
      {!isRecruiter(profile) && <AdminSidebar />}
      <div className={classnames("work_area", { "no-sidebar": isRecruiter(profile) })}>
        <div className="Settings floatm-l">
          <Switch>
            {!isEducational && <PrivateRoute requires="superadmin" exact path="/admin/departments/:page?" component={Settings} />}
            {canViewCompaniesField(organization) && <PrivateRoute requires="superadmin" exact path="/admin/companies/:page?" component={Settings} />}
            {canViewSkillsField(organization) && !isEducational && (<PrivateRoute requires="superadmin" exact path="/admin/skills/:page?" component={Settings} />)}
            {externalMappedIds && <PrivateRoute requires="superadmin" exact path="/admin/chq/mappers/roles" component={OrganizationMapperRoles} />}
            {externalMappedIds && <PrivateRoute requires="superadmin" exact path="/admin/mappers/:page?" component={SettingsJobMappers} />}
            {externalMappedIds && <PrivateRoute requires="superadmin" exact path="/admin/mappings/:type?/:page?" component={SettingsJobMappings} />}
            {eventsEnabled && (<PrivateRoute requires="superadmin" exact path="/admin/events/:page?" component={Events} />)}
            {employerBranding && <PrivateRoute requires="superadmin" exact path="/admin/landing-pages/:id" component={EmployerBranding} />}
            {employerBranding && <PrivateRoute requires="superadmin" exact path="/admin/landing-pages" component={LandingPageTable} />}
            <PrivateRoute requires="superadmin" exact path="/admin/feedback" component={Review} />
            <PrivateRoute requires="superadmin" exact path="/admin/flags/:page?" component={Review} />
            <PrivateRoute requires="superadmin" exact path="/admin/google" component={Integrations} />
            <PrivateRoute requires="superadmin" exact path="/admin/social" component={Integrations} />
            <PrivateRoute requires="superadmin" exact path="/admin/interests/:page?" component={Settings} />
            <PrivateRoute requires="superadmin" exact path="/admin/invites/create" component={UsersList} />
            <PrivateRoute requires="superadmin" exact path="/admin/locations/:page?" component={Settings} />
            <PrivateRoute requires="superadmin" exact path="/admin/microsoft" component={Integrations} />
            <PrivateRoute requires="superadmin" exact path="/admin/users" component={UsersList} />
            <PrivateRoute requires="superadmin" exact path="/admin/values/:page?" component={Settings} />
            <PrivateRoute requires="superadmin" exact path="/admin/widgets" component={Widgets} />
            <PrivateRoute requires="superadmin" exact path="/admin/widgets/:id/edit" component={Widgets} />
            <PrivateRoute requires="superadmin" exact path="/admin/widgets/create" component={Widgets} />
            <PrivateRoute requires="superadmin" exact path="/admin/widgets/disabled" component={Widgets} />
            {eventsEnabled && (<PrivateRoute requires="superadmin" exact path="/admin/analytics" component={Analytics} />)}
            <PrivateRoute requires="superadmin" exact path="/admin/analytics/internal/:page?" component={InternalAnalytics} />
            <PrivateRoute requires="superadmin" exact path="/admin/analytics/external/:page?" component={ExternalAnalytics} />
            <PrivateRoute requires="superadmin" exact path="/admin/analytics/social/:page?" component={SocialAnalytics} />
            <Route exact path="/admin/slack" render={() => <Integrations />} requires="slack" />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/organizations/:page?" component={Organizations} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/users" component={Users} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/slack" component={SlackUsers} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/ai-context" component={AIContext} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/default-prompts" component={DefaultPrompts} />
            <PrivateRoute requires="superadmin" exact path="/admin/templates/:page?" component={BuilderTemplates} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/virtual-backgrounds" component={VirtualBackgrounds} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/privacy-policies" component={PrivacyPolicies} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/organization-users" component={UsersWithoutOrganization} />
            <PrivateRoute requires="superadmin" exact path="/admin/chq/monitor-ai-usage" component={MonitorAIUsage} />
            <Route requires="admin" exact path="/admin/story-questions/:page?" render={() => <StoryQuestion />} />
            <Route requires="admin" exact path="/admin/story-reviews/pending/:page?" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/pending/story/:storyId" render={() => <StoryApprovals />} />
            <Route requires={["admin", "recruiter"]} exact path="/admin/story-reviews/approved/:page?" render={() => <StoryApprovals />} />
            <Route requires={["admin", "recruiter"]} exact path="/admin/story-reviews/approved/story/:storyId" render={() => <StoryApprovals />} />
            <Route requires={["admin", "recruiter"]} exact path="/admin/story-reviews/mapped/:page?" render={() => <StoryApprovals />} />
            <Route requires={["admin", "recruiter"]} exact path="/admin/story-reviews/mapped/story/:storyId" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/published/:page?" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/internal/:page?" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/internal/story/:storyId" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/archived/:page?" render={() => <StoryApprovals />} />
            <Route requires="admin" exact path="/admin/story-reviews/archived/story/:storyId" render={() => <StoryApprovals />} />
            {!isEducational && recognitionsEnabled && (
              <>
                <PrivateRoute requires="superadmin" exact path="/admin/recognition-types/:page?" component={RecognitionsOptions} />
                <PrivateRoute requires="superadmin" exact path="/admin/auto-recognitions" component={RecognitionsOptions} />
                <PrivateRoute requires="superadmin" exact path="/admin/pending-recognitions" component={RecognitionsOptions} />
              </>
            )}
            {isEducational && (
              <>
                <PrivateRoute requires="superadmin" exact path="/admin/programs/:page?" component={Settings} />
                <PrivateRoute requires="superadmin" exact path="/admin/class-years/:page?" component={Settings} />
                <PrivateRoute requires="superadmin" exact path="/admin/degrees/:page?" component={Settings} />
                <PrivateRoute requires="superadmin" exact path="/admin/clubs/:page?" component={Settings} />
              </>
            )}
            <Route component={NotFound} />
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
